import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useHistory, type HistoryEntry } from "../lib/history";
import { useUploads } from "../lib/uploads";
import { progressOf, useReplaceJobs } from "../lib/replaceJobs";
import { Icon } from "./icons";

// What the tool has been doing, and what it did. Replaces the corner toast,
// which by design vanished the moment nothing was running — so the only view of
// this work disappeared exactly when you wanted to look back at it.

const GROUP_ICON: Record<string, string> = {
  proof: "proofing", spec: "waveform", toc: "table",
  upload: "upload", delete: "trash", other: "info",
};

/** Where an entry takes you. The project hub unless the work has a face of its
 *  own — a proof has a report, a tech check has its own page, a ToC has a
 *  table. Jumping is the point; an entry you cannot follow is just a log line. */
function hrefFor(e: HistoryEntry): string | null {
  if (!e.project_id) return null;
  return e.tab ? `/projects/${e.project_id}/${e.tab}` : `/projects/${e.project_id}`;
}

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Today / Yesterday / a date — the grouping Chrome's history uses, and the one
 *  people actually search by ("it was some time yesterday"). */
function dayLabel(iso: string | null): string {
  if (!iso) return "Earlier";
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((+today - +d) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return new Date(iso).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
}

export function HistoryDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const { data, isLoading } = useHistory(projectId, open);
  const { items, cancel, retry } = useUploads();
  const replace = useReplaceJobs();

  // Uploads live in this browser, not on the server, so they are the one part
  // of "what is happening" that no endpoint can answer. They sit on top, where
  // the thing still running belongs.
  const live = items.filter((i) => i.status === "queued" || i.status === "uploading");
  const failed = items.filter((i) => i.status === "error");
  // Counted over EVERY file in the batch, not just the ones still going. The
  // toast averaged only the active ones, so each file that finished left the
  // denominator and the number could fall as the work progressed.
  const batch = items.filter((i) => i.status !== "canceled");
  const pct = batch.length
    ? Math.round(batch.reduce((a, i) => a + (i.status === "done" ? 100 : i.pct), 0) / batch.length)
    : 0;

  const entries = useMemo(() => {
    const all = data?.entries ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((e) =>
      `${e.verb} ${e.subject} ${e.project_name} ${e.detail}`.toLowerCase().includes(needle));
  }, [data, q]);

  const days = useMemo(() => {
    const out: { label: string; rows: HistoryEntry[] }[] = [];
    for (const e of entries) {
      const label = dayLabel(e.at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push(e);
      else out.push({ label, rows: [e] });
    }
    return out;
  }, [entries]);

  if (!open) return null;

  return (
    <>
      <div className="hist-backdrop" onClick={onClose} />
      <aside className="hist-drawer" aria-label="History">
        <div className="hist-head">
          <div>
            <h2>History</h2>
            <p className="muted">Everything the tool has done, newest first. Click any line to go there.</p>
          </div>
          <button className="hist-x" onClick={onClose} aria-label="Close history"><Icon name="x" size={18} /></button>
        </div>

        <div className="hist-filters">
          <div className="wf-search hist-search">
            <Icon name="search" size={13} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search history…" aria-label="Search history" />
          </div>
          <select value={projectId ?? ""} onChange={(e) => setProjectId(e.target.value || null)}
            aria-label="Filter by project">
            <option value="">All projects</option>
            {(data?.projects ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {(live.length > 0 || failed.length > 0) && (
          <div className="hist-live">
            <div className="hist-live-h">
              <span className="spinner" style={{ width: 13, height: 13 }} />
              {live.length > 0
                ? <>Uploading {live.length} of {batch.length} · <b>{pct}%</b></>
                : <>{failed.length} upload{failed.length === 1 ? "" : "s"} failed</>}
            </div>
            {[...live, ...failed].slice(0, 6).map((u) => (
              <div key={u.id} className={`hist-up${u.status === "error" ? " bad" : ""}`}>
                <span className="hist-up-n" title={u.filename}>{u.filename}</span>
                {u.status === "error"
                  ? <button className="btn ghost sm" onClick={() => retry(u.id)}>Retry</button>
                  : <>
                      <span className="hist-up-bar"><i style={{ width: `${u.pct}%` }} /></span>
                      <button className="btn ghost sm" onClick={() => cancel(u.id)}>Cancel</button>
                    </>}
              </div>
            ))}
          </div>
        )}

        {/* A file-set replacement runs in this browser too: under way, stopped
            by an error, or paused by a closed tab until its files are dropped
            again. One click brings its dialog back. */}
        {replace.jobs.length > 0 && (
          <div className="hist-live">
            {replace.jobs.map((j) => {
              const { total, done, pct } = progressOf(j);
              return (
                <div key={j.projectId} className={`hist-up${j.status === "stopped" ? " bad" : ""}`}>
                  <span className="hist-up-n" title={j.projectName}>
                    {j.status === "running" ? "Replacing files" : j.status === "stopped" ? "Replacement stopped" : "Replacement paused"}
                    {j.projectName ? ` · ${j.projectName}` : ""} · {done}/{total}
                  </span>
                  {j.status === "running" && <span className="hist-up-bar"><i style={{ width: `${pct}%` }} /></span>}
                  <button className="btn ghost sm" onClick={() => { replace.show(j.projectId); onClose(); }}>
                    {j.status === "running" ? "Show" : "Open"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="hist-list">
          {isLoading && <div className="muted hist-empty">Reading the record…</div>}
          {!isLoading && days.length === 0 && (
            <div className="hist-empty muted">
              {q ? "Nothing matches that." : "Nothing here yet — uploads, proofs and checks will appear as they happen."}
            </div>
          )}
          {days.map((d) => (
            <div key={d.label} className="hist-day">
              <div className="hist-day-h">{d.label}</div>
              {d.rows.map((e) => {
                const href = hrefFor(e);
                const body = (
                  <>
                    <span className={`hist-ico g-${e.group}${e.status === "failed" ? " bad" : ""}`}>
                      <Icon name={GROUP_ICON[e.group] || "info"} size={14} />
                    </span>
                    <span className="hist-main">
                      <span className="hist-line">
                        <b>{e.verb}</b>
                        {e.subject && <span className="hist-subj" title={e.subject}>{e.subject}</span>}
                        {e.live && <span className="hist-badge live">running{e.progress != null ? ` ${Math.round(e.progress)}%` : ""}</span>}
                        {e.status === "failed" && <span className="hist-badge bad">failed</span>}
                        {/* A worker that died leaves its row saying "running".
                            Saying so is kinder than a spinner that never stops. */}
                        {e.status === "stalled" && <span className="hist-badge bad">interrupted</span>}
                      </span>
                      {/* The stage only while it's RUNNING, where "aligning"
                          tells you where a job has got to. Once it's finished it
                          is a system word next to an icon and a bold verb that
                          both already said which service this was. */}
                      <span className="hist-sub">
                        {e.project_name || "—"}
                        {e.live && e.detail ? ` · ${e.detail}` : ""}
                        {e.error ? ` · ${e.error}` : ""}
                      </span>
                    </span>
                    <span className="hist-when">{when(e.at)}</span>
                  </>
                );
                return href
                  ? <Link key={e.id} to={href} className="hist-row" onClick={onClose}>{body}</Link>
                  : <div key={e.id} className="hist-row flat">{body}</div>;
              })}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
